import type { ReactNode } from 'react';

interface PromoBannerProps {
  title: ReactNode;
  description: string;
  /** Action slot — caller supplies a fully-formed Button (apply modal, link, …). */
  cta: ReactNode;
  /** Decorative image shown on the right (lg only). */
  image?: string;
  imageClassName?: string;
  /** Separate crop the design uses below `lg`, stacked under the CTA rather than
   *  placed beside the text (Figma node 5663:15271). Without it the banner has no
   *  artwork at all on phones, since `image` is desktop-only. */
  mobileImage?: string;
  /** Text and CTA alignment below `lg`. The app landing centres them; the
   *  merchant landing keeps the original left alignment, so this is opt-in. */
  mobileAlign?: 'start' | 'center';
  /** Scale the heading/description and swap in the mobile-specific gradient
   *  (Figma node 5663:15453) instead of using the desktop metrics at every
   *  width. Opt-in, because the merchant landing's banner has no verified
   *  mobile design yet and keeps the desktop treatment throughout. */
  mobileFluid?: boolean;
  /** Breakpoint where the side-by-side desktop layout takes over from the
   *  stacked mobile one. Defaults to `lg` (1024px); the app promo banner
   *  switches earlier, at `sm` (640px), so tablets already get the desktop
   *  treatment instead of the phone layout. */
  desktopBreakpoint?: 'sm' | 'lg';
}

const DESKTOP_LAYOUT_CLASSES = {
  sm: 'sm:h-[288px] sm:flex-row sm:items-start sm:justify-between sm:text-left',
  lg: 'lg:h-[288px] lg:flex-row lg:items-start lg:justify-between lg:text-left',
} as const;
const DESKTOP_TEXT_GRADIENT = {
  sm: 'sm:bg-gradient-to-br sm:from-[#9079ff] sm:to-[#5e4ac4]',
  lg: 'lg:bg-gradient-to-br lg:from-[#9079ff] lg:to-[#5e4ac4]',
} as const;
const DESKTOP_STACK_CLASSES = {
  sm: 'sm:items-start sm:justify-between',
  lg: 'lg:items-start lg:justify-between',
} as const;
const MOBILE_IMAGE_HIDDEN = { sm: 'sm:hidden', lg: 'lg:hidden' } as const;
const DESKTOP_IMAGE_VISIBLE_CLASSES = {
  sm: 'hidden absolute right-15 bottom-0 h-[90%] w-auto shrink-0 object-contain sm:block',
  lg: 'hidden absolute right-15 bottom-0 h-[90%] w-auto shrink-0 object-contain lg:block',
} as const;

// Fluid values scale linearly with viewport width between the two reference
// mockups — 402px (5663:15453) and 1440px (already verified against the
// design) — via the standard `clamp(min, intercept + slope*vw, max)` formula.
const FLUID_H2_SIZE = 'text-[clamp(20px,15.35px+1.156vw,32px)]';
const FLUID_H2_LEADING = 'leading-[clamp(28px,23.35px+1.156vw,40px)]';
const FLUID_DESCRIPTION_SIZE = 'text-[clamp(14px,13.23px+0.193vw,16px)]';
const FLUID_DESCRIPTION_LEADING = 'leading-[clamp(20px,18.45px+0.385vw,24px)]';

// The mobile gradient (Figma node 5663:15453) is a genuinely different shape
// from the desktop one — a bottom-anchored radial fade vs. a corner-to-corner
// linear one — not a scaled copy, so it's a hard swap at `lg` rather than a
// fluid blend. Decoded from the raw SVG gradientTransform matrix(0.5, -52,
// 30.448, 0.29277, 180, 520): the shear terms are within rounding noise of
// zero once the ~0.5° residual rotation is dropped, leaving a plain
// axis-aligned ellipse centred at bottom-middle.
const MOBILE_GRADIENT =
  'bg-[radial-gradient(82.3%_100%_at_50%_100%,#ded8ff_0%,#ad9cff_50%,#948fff_75%,#7b61ff_100%)]';
const DESKTOP_DESCRIPTION_OPACITY = { sm: 'sm:text-white/70', lg: 'lg:text-white/70' } as const;

/** Presentational gradient CTA banner. Knows nothing about the apply modal or
 *  downloads — the caller passes the CTA control via the `cta` slot. */
export function PromoBanner({
  title,
  description,
  cta,
  image,
  imageClassName,
  mobileImage,
  mobileAlign = 'start',
  mobileFluid = false,
  desktopBreakpoint = 'lg',
}: PromoBannerProps) {
  const centred = mobileAlign === 'center';

  return (
    <div
      className={`rounded-card-lg relative flex flex-col gap-8 overflow-hidden p-6 ${DESKTOP_LAYOUT_CLASSES[desktopBreakpoint]} ${
        mobileFluid
          ? `${MOBILE_GRADIENT} ${DESKTOP_TEXT_GRADIENT[desktopBreakpoint]}`
          : 'bg-gradient-to-br from-[#9079ff] to-[#5e4ac4]'
      } ${centred ? 'items-center text-center' : 'items-start'}`}
    >
      <div
        className={`z-10 flex h-full max-w-md flex-col gap-8 ${DESKTOP_STACK_CLASSES[desktopBreakpoint]} ${
          centred ? 'items-center' : ''
        }`}
      >
        <div className="flex flex-col gap-4">
          <h2
            className={`font-semibold tracking-tight text-white ${
              mobileFluid ? `${FLUID_H2_SIZE} ${FLUID_H2_LEADING}` : 'text-h2'
            }`}
          >
            {title}
          </h2>
          {/* Mobile dims this caption to 80% opacity vs. desktop's 70% (Figma
              nodes 5663:15453 / pre-existing desktop spec) — a plain breakpoint
              swap since opacity can't share the `vw`-based clamp() above
              (calc() has no notion of interpolating between two numbers via a
              length unit). */}
          <p
            className={
              mobileFluid
                ? `text-white/80 ${DESKTOP_DESCRIPTION_OPACITY[desktopBreakpoint]} ${FLUID_DESCRIPTION_SIZE} ${FLUID_DESCRIPTION_LEADING}`
                : 'text-base text-white/70'
            }
          >
            {description}
          </p>
        </div>
        {cta}
      </div>
      {mobileImage ? (
        <img
          src={mobileImage}
          alt=""
          loading="lazy"
          // Bleeds into the banner's own padding so the phones sit flush with the
          // bottom edge, the way the design crops them. `max-w-none` is required —
          // Tailwind's preflight sets `img { max-width: 100% }`, which silently
          // capped this at the padding box's own width (322px) instead of the
          // card's full 370px, leaving a 24px gap on each side instead of a flush
          // edge-to-edge bleed.
          className={`-mx-6 -mb-6 w-[calc(100%+3rem)] max-w-none object-contain ${MOBILE_IMAGE_HIDDEN[desktopBreakpoint]}`}
        />
      ) : null}
      {image ? (
        <img
          src={image}
          alt=""
          loading="lazy"
          className={imageClassName ?? DESKTOP_IMAGE_VISIBLE_CLASSES[desktopBreakpoint]}
        />
      ) : null}
    </div>
  );
}
